import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import {
  MapPin, Bed, Bath, Maximize, Home, Calendar, Building,
  Phone, Mail, MessageCircle, Heart, ArrowLeft, Check
} from 'lucide-react';
import { propertyService } from '../services/propertyService';
import { requestService } from '../services/requestService';
import { toast } from 'sonner';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadProperty();
    checkFavorite();
  }, [id]);

  const loadProperty = async () => {
    try {
      setLoading(true);
      const data = await propertyService.getPropertyById(id);
      setProperty(data);
      setFormData(prev => ({
        ...prev,
        message: `Bonjour, je suis intéressé par ${data.titre}`
      }));
    } catch (error) {
      console.error('Error loading property:', error);
      toast.error('Erreur lors du chargement du bien');
      navigate('/properties');
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    setIsFavorite(favorites.includes(id));
  };

  const toggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');

    if (isFavorite) {
      const newFavorites = favorites.filter(favId => favId !== id);
      localStorage.setItem('favorites', JSON.stringify(newFavorites));
      setIsFavorite(false);
      toast.success('Retiré des favoris');
    } else {
      favorites.push(id);
      localStorage.setItem('favorites', JSON.stringify(favorites));
      setIsFavorite(true);
      toast.success('Ajouté aux favoris');
    }
  };

  const handleCall = () => {
    window.location.href = 'tel:+221783352626';
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent(`Bonjour, je suis intéressé par ${property?.titre}`);
    const whatsappUrl = `https://wa.me/221783352626?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await requestService.createRequest({
        nomClient: formData.nom,
        prenomClient: formData.prenom,
        emailClient: formData.email,
        telephoneClient: formData.telephone,
        message: formData.message,
        typeDemande: 'Bien',
        idBienConcerne: id
      });

      toast.success('Message envoyé ! Nous vous contacterons très bientôt.');
      setFormData({
        nom: '',
        prenom: '',
        email: '',
        telephone: '',
        message: `Bonjour, je suis intéressé par ${property?.titre}`
      });
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-8 w-32 mb-8" />
          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2">
              <Skeleton className="h-96 w-full rounded-xl" />
            </div>
            <Skeleton className="h-96 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Bien non trouvé</h2>
          <Button onClick={() => navigate(-1)}>Retour</Button>
        </div>
      </div>
    );
  }

  const imageUrls = property.images?.map(img => img.urlImage) || [];
  const displayPrice = property.prixSurDemande
    ? 'Sur demande'
    : property.prix
      ? `${parseInt(property.prix).toLocaleString()} ${property.devise || 'FCFA'}`
      : 'Sur demande';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Back Button */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </div>

      {/* Image Gallery */}
      <section className="bg-white">
        <div className="container mx-auto px-4 py-8">
          <div className="grid md:grid-cols-3 gap-4">
            {/* Main Image */}
            <div className="md:col-span-2">
              <div className="relative rounded-xl overflow-hidden h-96 md:h-[500px]">
                <img
                  src={imageUrls[currentImageIndex] || '/placeholder.jpg'}
                  alt={property.titre}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={toggleFavorite}
                  className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-3 rounded-full hover:bg-white transition-all"
                >
                  <Heart className={`w-6 h-6 ${isFavorite ? 'fill-red-500 text-red-500' : 'text-slate-600'}`} />
                </button>
              </div>
              {/* Thumbnails */}
              {imageUrls.length > 1 && (
                <div className="grid grid-cols-3 gap-4 mt-4">
                  {imageUrls.slice(0, 3).map((image, index) => (
                    <div
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`rounded-lg overflow-hidden cursor-pointer h-24 ${
                        currentImageIndex === index ? 'ring-4 ring-amber-600' : 'opacity-70 hover:opacity-100'
                      } transition-all`}
                    >
                      <img src={image} alt={`${property.titre} - ${index + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Info Card */}
            <div>
              <Card className="sticky top-4">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <span className="inline-block bg-amber-100 text-amber-800 text-sm px-3 py-1 rounded-full font-medium">
                      {property.typeBien}
                    </span>
                  </div>
                  <h1 className="text-2xl font-bold text-slate-900 mb-4">{property.titre}</h1>
                  <div className="flex items-center gap-2 text-slate-600 mb-4">
                    <MapPin className="w-5 h-5 flex-shrink-0" />
                    <span>{property.localisation?.zone}, {property.localisation?.ville}</span>
                  </div>
                  <div className="text-3xl font-bold text-amber-600 mb-6">
                    {displayPrice}
                  </div>

                  <div className="space-y-3 mb-6">
                    <Button onClick={handleCall} variant="outline" className="w-full">
                      <Phone className="w-4 h-4 mr-2" />
                      Appeler maintenant
                    </Button>
                    <Button onClick={handleWhatsApp} className="w-full bg-green-500 hover:bg-green-600">
                      <MessageCircle className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>

                  <div className="border-t pt-4 text-sm text-slate-600">
                    <p className="flex items-center gap-2 mb-2">
                      <Phone className="w-4 h-4" />
                      +221 78 335 26 26
                    </p>
                    <p className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      contact@pamimmo.sn
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Property Details */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="md:col-span-2 space-y-8">
              {/* Characteristics */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6">Caractéristiques</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    {property.surface && (
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Maximize className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                          <div className="text-sm text-slate-600">Surface</div>
                          <div className="font-semibold text-slate-900">{property.surface}m²</div>
                        </div>
                      </div>
                    )}
                    {property.chambres && (
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Bed className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                          <div className="text-sm text-slate-600">Chambres</div>
                          <div className="font-semibold text-slate-900">{property.chambres}</div>
                        </div>
                      </div>
                    )}
                    {property.sallesDeBain && (
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Bath className="w-6 h-6 text-amber-600" />
                        </div>
                        <div>
                          <div className="text-sm text-slate-600">Salles de bain</div>
                          <div className="font-semibold text-slate-900">{property.sallesDeBain}</div>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Description */}
              <Card>
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">Description</h2>
                  <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                    {property.description}
                  </p>
                </CardContent>
              </Card>

              {/* Features */}
              {(() => {
                const equipements = [
                  { key: 'ascenseur', label: 'Ascenseur' },
                  { key: 'climatisation', label: 'Climatisation' },
                  { key: 'jardin', label: 'Jardin' },
                  { key: 'piscine', label: 'Piscine' },
                  { key: 'groupeElectrogene', label: 'Groupe électrogène' },
                  { key: 'videoSurveillance', label: 'Vidéo surveillance' },
                  { key: 'titreFoncier', label: 'Titre foncier' },
                  { key: 'routeGoudronnee', label: 'Route goudronnée' },
                ];
                const activeEquipements = equipements.filter(e => property[e.key]);
                if (activeEquipements.length === 0) return null;
                return (
                  <Card>
                    <CardContent className="p-6">
                      <h2 className="text-2xl font-bold text-slate-900 mb-4">Équipements et prestations</h2>
                      <div className="grid md:grid-cols-2 gap-3">
                        {activeEquipements.map((feature) => (
                          <div key={feature.key} className="flex items-center gap-2">
                            <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                            <span className="text-slate-700">{feature.label}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}
            </div>

            {/* Contact Form */}
            <div>
              <Card className="sticky top-4">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold text-slate-900 mb-4">Demande d'information</h2>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="prenom" className="block text-sm font-medium text-slate-700 mb-2">
                        Prénom *
                      </label>
                      <input
                        id="prenom"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        required
                        placeholder="Votre prénom"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="nom" className="block text-sm font-medium text-slate-700 mb-2">
                        Nom *
                      </label>
                      <input
                        id="nom"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                        placeholder="Votre nom"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                        Email *
                      </label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="votre@email.com"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="telephone" className="block text-sm font-medium text-slate-700 mb-2">
                        Téléphone
                      </label>
                      <input
                        id="telephone"
                        name="telephone"
                        type="tel"
                        value={formData.telephone}
                        onChange={handleChange}
                        placeholder="+221 77 123 45 67"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-lg py-6"
                    >
                      {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PropertyDetail;
